import apiClient from '@/lib/api-client'

// Types mirror the backend category-tree serializers (T-279 / MPH-155).

import type { MarketplaceSource } from './sources'

export { MARKETPLACE_SOURCE_LABELS, type MarketplaceSource } from './sources'
export type CategoryTagStatus = 'selling' | 'want_to_enter' | 'not_interested'

export type CategoryTreeAccess = { enabled: boolean }

export type CategoryAuthor = { id: string; email: string; name: string }

export type CategoryPathItem = { external_category_id: string; title: string }

export type CategoryNode = {
  kind?: 'category' | 'collection'
  external_category_id: string
  title: string
  url?: string
  depth: number
  has_children: boolean
  child_count?: number
  descendant_count?: number
  owned: boolean
  own_product_count: number
  tag_status: CategoryTagStatus | null
  comment_count: number
  path?: CategoryPathItem[]
}

export type CategoryComment = {
  id: string
  body: string
  created_at: string
  created_by: CategoryAuthor | null
}

const BASE = '/api/v1/tracker/categories'

export type ScoutProduct = {
  category_title?: string; category_url?: string; captured_at?: string
  product_id: string; position: number | null; title: string; url: string
  image_url: string; price_text: string; availability_text: string; seller: string; seller_url?: string
  is_advertised: boolean; position_delta: number | null
  listing_reviews?: number | null; listing_rating?: number | null
  listing_price_delta?: number | string | null
  metrics_at: string | null; baseline_at: string | null
  metrics: {
    price: number | null; promo_price: number | null; old_price: number | null; discount: number | null
    wishlist_count: number | null; comments_count: number | null; comments_mark: number | string | null
    orders_count: number | null; same_offers_count: number | null; same_offers_min_price: number | null
    is_available: boolean | null; is_fulfillment: boolean | null; is_top_sale: boolean | null
  } | null
  deltas: Partial<Record<'price' | 'promo_price' | 'discount' | 'wishlist_count' | 'comments_count' | 'comments_mark' | 'orders_count' | 'same_offers_count' | 'same_offers_min_price', number | string | null>>
}

export type CategoryProducts = {
  selected_categories?: CategoryPathItem[]
  path?: CategoryPathItem[]
  count: number; collected_count: number; page: number; page_size: number; has_next: boolean
  category_count?: number
  captured_at: string | null
  category_url: string
  position_baseline_at?: string | null
  results: ScoutProduct[]
}

export type ScoutFilters = {
  categories?: string
  q?: string; sort?: string; price_min?: string; price_max?: string; reviews_min?: string
  wishlist_min?: string; rating_min?: string; has_metrics?: boolean; page?: number
}

export async function fetchCategoryProducts(source: MarketplaceSource, externalId: string | null, filters: ScoutFilters = {}): Promise<CategoryProducts> {
  const params = Object.fromEntries(Object.entries({ source, ...filters }).filter(([, value]) => value !== '' && value !== undefined))
  const { data } = await apiClient.get<CategoryProducts>(
    externalId === null ? `${BASE}/products/` : `${BASE}/${encodeURIComponent(externalId)}/products/`, { params },
  )
  return data
}

export async function fetchCategoryAccess(): Promise<CategoryTreeAccess> {
  const { data } = await apiClient.get<CategoryTreeAccess>(`${BASE}/access/`)
  return data
}

export async function fetchTree(
  source: MarketplaceSource,
  parent?: string | null,
): Promise<CategoryNode[]> {
  const { data } = await apiClient.get<{ results: CategoryNode[] }>(`${BASE}/tree/`, {
    params: { source, parent: parent ?? '' },
  })
  return data.results
}

export async function searchCategories(
  source: MarketplaceSource,
  q: string,
  opts?: { status?: CategoryTagStatus | ''; owned?: boolean },
): Promise<CategoryNode[]> {
  const { data } = await apiClient.get<{ results: CategoryNode[] }>(`${BASE}/search/`, {
    params: {
      source,
      q,
      status: opts?.status ?? '',
      owned: opts?.owned ? 'true' : '',
    },
  })
  return data.results
}

export type CategoryTagResult = {
  status: CategoryTagStatus | null
  updated_by?: CategoryAuthor | null
  updated_at?: string
}

export async function setCategoryTag(
  source: MarketplaceSource,
  externalId: string,
  status: CategoryTagStatus | null,
): Promise<CategoryTagResult> {
  const { data } = await apiClient.put<CategoryTagResult>(
    `${BASE}/${encodeURIComponent(externalId)}/tag/`,
    { source, status },
  )
  return data
}

export async function fetchComments(
  source: MarketplaceSource,
  externalId: string,
): Promise<CategoryComment[]> {
  const { data } = await apiClient.get<{ results: CategoryComment[] }>(
    `${BASE}/${encodeURIComponent(externalId)}/comments/`,
    { params: { source } },
  )
  return data.results
}

export async function addComment(
  source: MarketplaceSource,
  externalId: string,
  body: string,
): Promise<CategoryComment> {
  const { data } = await apiClient.post<CategoryComment>(
    `${BASE}/${encodeURIComponent(externalId)}/comments/`,
    { source, body },
  )
  return data
}

export async function deleteComment(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/comments/${id}/`)
}
